// Castintech dashboard — Event Queue Health view (Faz 39 PR-A).
// Backend: GET /castintech-api/event-queue/stats (in-memory snapshot).
// Goruntuler: queue depth, processing, processed, failed, retries, workers,
// processed_per_min ve KNOWN_EVENT_TYPES whitelist'i.
//
// Tasarim: D3/Chart.js bagimliligi YOK — pure DOM (runtime-health pattern).
// Render policy: page'e gecisinde loadEventQueue() cagrilir; ardindan 30s
// otomatik refresh (page acik kaldigi surece). Page kapaninca timer durur.
//
// ADR: wiki/decisions/faz39-pr-a-sse-bridge-altyapi.md

import { apiFetch } from '../api.js';
import { escHtml, $ } from '../utils.js';

interface EventQueueStatsPayload {
  depth: number;
  processing: number;
  processed: number;
  failed: number;
  retries: number;
  workers: number;
  max_retries: number;
  processed_per_min: number;
  known_event_types: string[];
  generated_at: string;
}

let _eqPayload: EventQueueStatsPayload | null = null;
let _eqTimer: ReturnType<typeof setInterval> | null = null;

/**
 * Backend'den son snapshot'i ceker ve renderEventQueue cagrir.
 */
export async function loadEventQueue(): Promise<void> {
  const sub = $('event-queue-sub');
  if (sub) sub.textContent = 'queue durumu yukleniyor...';
  try {
    const r = await apiFetch('/castintech-api/event-queue/stats');
    if (!r.ok) throw new Error('event-queue/stats HTTP ' + r.status);
    _eqPayload = (await r.json()) as EventQueueStatsPayload;
    renderEventQueue();
  } catch (e) {
    if (sub) sub.textContent = 'hata: ' + (e as Error).message;
  }
}

/** 30s otomatik refresh timer baslat. */
export function startEventQueueTimer(): void {
  stopEventQueueTimer();
  _eqTimer = setInterval(() => {
    void loadEventQueue();
  }, 30_000);
}

/** Refresh timer durdur. */
export function stopEventQueueTimer(): void {
  if (_eqTimer) {
    clearInterval(_eqTimer);
    _eqTimer = null;
  }
}

/** Mevcut payload'i view'a render eder. */
export function renderEventQueue(): void {
  const sub = $('event-queue-sub');
  if (!_eqPayload) {
    if (sub) sub.textContent = '— veri yok';
    return;
  }
  const p = _eqPayload;
  const generated = new Date(p.generated_at).toLocaleString('tr-TR');
  if (sub) {
    sub.textContent = `${p.processed} processed • ${p.failed} failed • ${generated}`;
  }
  renderMetrics(p);
  renderTypes(p.known_event_types);
}

function renderMetrics(p: EventQueueStatsPayload): void {
  const root = $('event-queue-metrics');
  if (!root) return;
  const tile = (label: string, value: number | string, severity?: 'ok' | 'warn' | 'danger') => {
    const cls = severity ? ' eq-tile-' + severity : '';
    return `<div class="eq-tile${cls}">
      <div class="eq-tile-value">${escHtml(String(value))}</div>
      <div class="eq-tile-label">${escHtml(label)}</div>
    </div>`;
  };
  const depthSev = p.depth > 50 ? 'danger' : p.depth > 10 ? 'warn' : 'ok';
  const failedSev = p.failed > 0 ? 'danger' : 'ok';
  root.innerHTML = [
    tile('Queue depth', p.depth, depthSev),
    tile('Processing', p.processing),
    tile('Processed (toplam)', p.processed, 'ok'),
    tile('Processed / min', p.processed_per_min),
    tile('Retries', p.retries, p.retries > 0 ? 'warn' : 'ok'),
    tile('Failed (final)', p.failed, failedSev),
    tile('Workers', p.workers),
    tile('Max retries', p.max_retries),
  ].join('');
}

function renderTypes(types: string[]): void {
  const root = $('event-queue-types');
  if (!root) return;
  if (!types || !types.length) {
    root.innerHTML = '<div class="eq-empty">Whitelist bos.</div>';
    return;
  }
  root.innerHTML = '<ul class="eq-types">' +
    types.map((t) => `<li><code>${escHtml(t)}</code></li>`).join('') +
    '</ul>';
}

/** Yenile butonunu bagla. */
export function bindEventQueueRefresh(): void {
  const btn = document.getElementById('event-queue-refresh-btn');
  if (btn) {
    btn.addEventListener('click', () => {
      void loadEventQueue();
    });
  }
}
